import { useEffect, useMemo, useRef, useState } from 'react'

import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { PlanTimeframe } from '@ebalance/proto/bfftypes/v1/plan_submission_pb'
import { type Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router'

import {
  Alert,
  Select,
  type SelectOption,
  TertiaryButton,
  fontSizes,
} from '@enechain/ecloud-designsystem'

import { AlertActions } from '~/components/common/AlertActions'
import { CompactCivilDateField } from '~/components/common/CompactCivilDateField'
import {
  FieldErrorText,
  FieldLabel,
  FieldRoot,
} from '~/components/common/Field'
import { Modal } from '~/components/common/Modal'
import { SaveCancelFooter } from '~/components/common/Modal/SaveCancelFooter'
import { RequiredMark } from '~/components/common/RequiredMark'
import {
  AREA_CODE_OPTIONS,
  convertAreaLabel,
  occtoAreaDigit,
} from '~/models/area'
import { civilDateToDate, dateToCivilDate } from '~/models/civilDate'
import { toProtoDate } from '~/models/dateTime'
import {
  type BgValidityPeriod,
  type DemandProcurementBgItem,
  isDemandProcurementBgActiveOn,
} from '~/models/planSubmission/isDemandProcurementBgActiveOn'
import { timeframeForTargetDate } from '~/models/planSubmission/targetDate'
import { routePaths } from '~/routes/routePaths'

export type CreatePlanDialogInput = {
  timeframe: PlanTimeframe
  areaCode: AreaCode
  targetPeriodStartDate: ProtoDate
  submissionEntityCode: string
}

type Props = {
  isOpen: boolean
  /**
   * 一覧の対象日フィルタ現在値 (`usePersistedProcurementPlanFilters` の
   * `targetDate`、非 undefined)。ダイアログを開いた瞬間 (isOpen の false→true
   * エッジ) にこの値を対象日の初期値としてコピーする (AC-PLC-040、
   * `tmp/designing-feature/create-dialog-target-date-initial-value.md`
   * Decision #1)。setter は受け取らない = 値のコピーのみで、ダイアログ内の
   * その後の変更は一覧側の state に一切波及しない (Decision #4、design §2 の
   * 「今日」ボタン断面不整合の構造的解消と同じ分離原則)。
   */
  initialTargetDate: Date
  /**
   * 開いた瞬間のエリア・BG の初期値 (省略時はエリア = 候補の先頭、BG = 未選択)。
   * 作成失敗の計画の詳細から「計画を作り直す」で開く時に、当該計画のエリア・BG を
   * 渡す (AC-PLC-020-2。AC-PLC-040 の初期値に代える)。候補データの取得が終わる
   * までは初期値を保ち、取得後に候補に無ければ省略時と同じ既定に戻る。
   */
  initialSelection?: { areaCode: AreaCode; bgCode: string }
  /**
   * BG マスタの全件 (area 候補判定 + BG select の候補に使う。bgCode の末尾 1 桁が
   * area digit)。
   */
  bgMasterItems: DemandProcurementBgItem[]
  /** BG マスタ本体の有効期間。対象日フィルタの積判定に使う。 */
  bgMasterPeriods: ReadonlyMap<string, BgValidityPeriod>
  /** BG マスタ本体の BG 名称 (bgCode → bgName)。BG 選択肢のラベルに使う。 */
  bgNames: ReadonlyMap<string, string>
  /** 組織の業務エリア設定 (= 選択可能なエリアの上限集合)。 */
  businessAreas: AreaCode[]
  /**
   * 候補データ (BG マスタ一覧・業務エリア設定) の取得が成功して確定したか。
   * 読み込み中・取得失敗の間は `bgMasterItems`/`businessAreas` が未確定の空配列に
   * なり得るため、この値が false の間はエリア候補 0 件の案内 (Decision #5) を出さない
   * (未確定を「確定 0 件」と誤認して個人設定修正へ誤誘導するのを防ぐ)。既定 true。
   */
  isCandidateDataReady?: boolean
  isCreating: boolean
  onClose: () => void
  onCreate: (input: CreatePlanDialogInput) => void
}

/**
 * 需要調達計画 作成ダイアログ (AC-09/AC-10/AC-11)。
 *
 * 既存インライン UI (一覧画面のエリアフィルタと計画作成のエリア選択が同一
 * state を共有していた) を置換し、断面・対象日・エリア・BG の 4 項目を
 * 一覧のフィルタ state と完全に分離した独立フォームとして持つ (= 「今日」
 * ボタンの断面不整合を構造的に解消、設計書 §2)。
 *
 * データ取得 (BG マスタ一覧・業務エリア設定) は呼び出し側 (index.tsx) の
 * 責務とし、本コンポーネントは props 駆動の純粋な UI として保つ
 * (= F2 コンポーネントテストが Connect Transport mock 不要になる)。
 *
 * オーバーレイクリックでは閉じない (= `~/components/common/Modal` が
 * `closeOnInteractOutside={false}` を持つため AC-11 は自動的に満たされる)。
 *
 * 計画断面は「当日・翌日・翌々日」の単一固定ラベル (非操作、Select 廃止) で表示する
 * (procurement-create-dialog-section-target-date-approval 設計 Decision #1/#2、
 * #7149 で「当日」を含めるよう拡張〈issue #6816 Discussion 決定行 B 案採用〉:
 * この作成ダイアログに FIT 断面は proto 構造上存在せず、断面の選択肢は
 * 「当日・翌日・翌々日」の 1 つに統合されたため、一覧タブとの初期選択同期・
 * 当日フォールバックの丸めロジックは不要 = 常に同一値)。対象日は開いた瞬間に
 * `initialTargetDate` (= 一覧の対象日フィルタ現在値) を初期値としてコピーし
 * (AC-PLC-040、issue #7265、`tmp/designing-feature/
 * create-dialog-target-date-initial-value.md`)、その後はダイアログ内で独立に
 * 変更できる (setter は受け取らないため一覧側へは波及しない)。許容範囲は今日
 * 以降の任意の未来日で (AC-PLC-039/040、issue #6816 Decision B)、ユーザーが
 * 手動でクリアした場合の未入力・範囲外 (過去日、初期値コピーが過去日の場合を
 * 含む) いずれも「作成」を拒否して対象日欄にエラー表示する。送信 timeframe は
 * 対象日から導出する
 * (AC-PLC-043、`timeframeForTargetDate`。当日=TODAY・明後日以降=DAY_AFTER_TOMORROW clamp
 * の根拠〈取込経路 deriveImportTimeframe との business key 一致〉と基準日 JST 壁時計は
 * 同関数の docstring 参照)。
 *
 * 承認フロー設定 UI は持たず常に「承認なし」固定とし、非適用の注記
 * (`Alert` status=info) をダイアログ先頭に表示する (AC-007, #6526)。
 */
export const CreatePlanDialog = ({
  isOpen,
  initialTargetDate,
  initialSelection,
  bgMasterItems,
  bgMasterPeriods,
  bgNames,
  businessAreas,
  isCandidateDataReady = true,
  isCreating,
  onClose,
  onCreate,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  const [targetDate, setTargetDate] = useState<Date | undefined>(undefined)
  const [targetDateError, setTargetDateError] = useState<string | undefined>(
    undefined,
  )
  const [selection, setSelection] = useState<
    { areaCode: AreaCode; bgCode: string } | undefined
  >()
  // 「作成」 を押した時にエリア / BG が未選択だったか (AC-PLC-042)。 未選択の欄に
  // エラーを出し、 その欄を選び直すまで残す。
  const [showSelectionError, setShowSelectionError] = useState(false)

  const activeBgMasterItems = useMemo(
    () => activeItemsOn(bgMasterItems, bgMasterPeriods, targetDate),
    [bgMasterItems, bgMasterPeriods, targetDate],
  )
  const businessAreaSet = useMemo(() => new Set(businessAreas), [businessAreas])
  const availableAreas = useMemo(
    () => selectableAreas(activeBgMasterItems, businessAreaSet),
    [activeBgMasterItems, businessAreaSet],
  )

  // 対象日の有効期間フィルタを掛けない「登録済みエリア」= 需要調達計画マスタ ∩
  // 業務エリア設定。エリア候補 0 件時に、原因が「未登録 (マスタ or 業務エリア)」か
  // 「選択中の対象日が有効期間外」かを出し分けるために使う。activeBgMasterItems は
  // 対象日で有効期間フィルタ (isDemandProcurementBgActiveOn / AC-PLC-047・041-1) を
  // 掛けるため、マスタ・業務エリアが登録済みでも対象日次第で availableAreas が 0 件に
  // なり得る (issue #6353 / PR #6948 ikdene 指摘)。ここは日付非依存の raw な
  // bgMasterItems を使う。
  const registeredAreaDigitsAllDates = useMemo(
    () => new Set(bgMasterItems.map((item) => item.bgCode.slice(-1))),
    [bgMasterItems],
  )
  const hasRegisteredArea = useMemo(
    () =>
      AREA_CODE_OPTIONS.some(
        (area) =>
          businessAreaSet.has(area) &&
          registeredAreaDigitsAllDates.has(occtoAreaDigit(area) ?? ''),
      ),
    [businessAreaSet, registeredAreaDigitsAllDates],
  )

  const wasOpenRef = useRef(false)

  useEffect(() => {
    const wasOpen = wasOpenRef.current
    wasOpenRef.current = isOpen
    if (!isOpen || wasOpen) return
    setTargetDate(initialTargetDate)
    setTargetDateError(undefined)
    setShowSelectionError(false)
    setSelection(undefined)
  }, [isOpen, initialTargetDate])

  const preferredSelection = selection ?? initialSelection
  const preferredAreaCode = preferredSelection?.areaCode
  const areaCode =
    preferredAreaCode !== undefined &&
    availableAreas.includes(preferredAreaCode)
      ? preferredAreaCode
      : (availableAreas[0] ?? AreaCode.UNSPECIFIED)

  // エリア選択肢は候補 (availableAreas) のみで構築する (AC-PLC-041)。
  // 業務エリア外・需要 BG 未登録のエリアは選択肢自体から除外し (disabled 表示は廃止)、
  // design (`design/plan/procurement.html` L342-347, #5597) / conventions.md
  // 「業務エリアフィルタの原則」に合わせる。
  const areaOptions: SelectOption[] = useMemo(
    () =>
      availableAreas.map((area) => ({
        value: String(area),
        label: convertAreaLabel(area),
      })),
    [availableAreas],
  )
  const selectedAreaOption =
    areaOptions.find((opt) => opt.value === String(areaCode)) ?? null

  // BG 選択肢 = 選択中エリアの需要調達計画マスタ BG。対象日入力時は、需要調達
  // 計画マスタと BG マスタ本体の両方の閉区間を満たすものだけを出す (AC-PLC-041-1)。
  // ラベルは「BG コード + BG 名称」(design `procurement.html` populateCreateBgOptions、
  // #9696)。名称が無い BG はコードのみ。
  const bgOptions: SelectOption[] = useMemo(() => {
    const areaDigit = occtoAreaDigit(areaCode)
    if (areaDigit === undefined) return []
    return activeBgMasterItems
      .filter((item) => item.bgCode.slice(-1) === areaDigit)
      .map((item) => {
        const bgName = bgNames.get(item.bgCode) ?? ''
        return {
          value: item.bgCode,
          label: bgName === '' ? item.bgCode : `${item.bgCode} ${bgName}`,
        }
      })
  }, [activeBgMasterItems, areaCode, bgNames])

  const bgCode =
    areaCode === preferredSelection?.areaCode ? preferredSelection.bgCode : ''
  const selectedBgOption = bgOptions.find((opt) => opt.value === bgCode) ?? null

  const resolvedBgCode = selectedBgOption?.value ?? ''
  if (
    isOpen &&
    isCandidateDataReady &&
    (selection === undefined ||
      selection.areaCode !== areaCode ||
      selection.bgCode !== resolvedBgCode)
  ) {
    setSelection({ areaCode, bgCode: resolvedBgCode })
  }

  // 選べるエリアが 0 件の間は「作成」 を非活性にし、 押せない理由を画面に文で出す
  // (AC-PLC-061)。 取得が未確定 (読み込み中・失敗) の間は未取得の空配列を「確定 0 件」 と
  // 誤認しないよう理由を出さない。 0 件の原因は hasRegisteredArea で 2 通りに分かれ、
  // 出す場所も分ける (UI 辞書 components/action-reason.md 区分 1。 フッターに押せない理由の文は出さない):
  //   - hasRegisteredArea=false: 業務エリアに需要 BG が 1 件も無い (AC-PLC-041) = 設定の不足。
  //     ダイアログ本文の上部に info の Alert を出し、 直す画面 (広域機関・個人設定) を
  //     Alert の右端のボタンで開く (文中の語をリンクにしない。 alert-placement.md §4)
  //   - hasRegisteredArea=true: マスタ・業務エリアは登録済みだが、 入力中の対象日が
  //     マスタ / BG の有効期間外 (AC-PLC-047) = 日付を変えれば直る。「対象日」 の欄のエラー
  const hasNoAreaCandidates = isCandidateDataReady && areaOptions.length === 0
  const isAreaUnregistered = hasNoAreaCandidates && !hasRegisteredArea
  const isTargetDateOutOfValidity = hasNoAreaCandidates && hasRegisteredArea
  // 対象日の欄に出すエラー。 押した時の入力エラー (未入力・過去日) を優先する。
  const targetDateFieldError =
    targetDateError ??
    (isTargetDateOutOfValidity
      ? t('page:procurementPlanList.createDialog.areaEmptyDateMessage')
      : undefined)

  // 必須の未選択 (AC-PLC-042) では「作成」 を非活性にせず、 押した時に未選択の欄へ
  // エラーを出す。 送信値は「画面に実際に出ている選択」= 解決済み option から導出する。
  const handleCreate = (): void => {
    // 非活性の「作成」 とハンドラの二重ガード (AC-PLC-061)
    if (hasNoAreaCandidates) return
    const isSelectionMissing =
      selectedAreaOption === null || selectedBgOption === null
    setShowSelectionError(isSelectionMissing)
    // 対象日は開いた瞬間に initialTargetDate で自動初期化されるが、ユーザーが
    // 手動でクリアした場合の未入力での「作成」はここで拒否する
    // (AC-PLC-063 + `plan-common.md` §4 バリデーション表)。 エリア / BG の未選択と
    // 同時に起きた時は、 両方の欄にエラーを出してから止める。
    if (targetDate === undefined) {
      setTargetDateError(
        t('page:procurementPlanList.createDialog.targetDateRequiredError'),
      )
      return
    }
    // 対象日の許容範囲は今日以降の任意の未来日 (AC-PLC-039/040、issue #6816
    // Decision B)。範囲外 (過去日) は `timeframeForTargetDate` が `undefined` を
    // 返すのでそのまま拒否判定に使う (= 範囲判定専用の別関数を持たない)。
    const timeframe = timeframeForTargetDate(targetDate)
    if (timeframe === undefined) {
      setTargetDateError(
        t('page:procurementPlanList.createDialog.targetDateOutOfRangeError'),
      )
      return
    }
    if (selectedAreaOption === null || selectedBgOption === null) return
    onCreate({
      timeframe,
      areaCode,
      targetPeriodStartDate: toProtoDate(targetDate),
      submissionEntityCode: selectedBgOption.value,
    })
  }

  const selectionErrorText = t(
    'page:procurementPlanList.createDialog.areaBgRequiredError',
  )
  const isAreaInvalid = showSelectionError && selectedAreaOption === null
  const isBgInvalid = showSelectionError && selectedBgOption === null

  // 非活性の「作成」 には同じ理由のツールチップを足す (action-reason.md 区分 1 の
  // 「設定の不足で非活性にしたボタンには、 同じ理由のツールチップを足してよい」)
  const createDisabledReason = isAreaUnregistered
    ? t('page:procurementPlanList.createDialog.areaEmptyMessage')
    : isTargetDateOutOfValidity
      ? t('page:procurementPlanList.createDialog.areaEmptyDateMessage')
      : undefined

  const footer = (
    <SaveCancelFooter
      isPending={isCreating}
      isSaveDisabled={!isCandidateDataReady || hasNoAreaCandidates}
      onCancel={onClose}
      onSave={handleCreate}
      saveDisabledReason={createDisabledReason}
      saveLabel={t('page:procurementPlanList.createDialog.createButton')}
    />
  )

  return (
    <Modal
      footer={footer}
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      title={t('page:procurementPlanList.createDialog.title')}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {/* AC-PLC-061: 業務エリアに需要 BG が 1 件も無い (設定の不足) 時の押せない理由。
            作れない理由なので、 下の注記より先 (業務上の優先度の順。 alert-placement.md §2) */}
        {isAreaUnregistered && (
          <Alert
            status="info"
            title={t('page:procurementPlanList.createDialog.areaEmptyMessage')}
          >
            <AlertActions>
              <TertiaryButton asChild size="sm">
                <RouterLink to={`${routePaths.adminOccto.path}?tab=proc-plan`}>
                  {t('page:procurementPlanList.createDialog.openOccto')}
                </RouterLink>
              </TertiaryButton>
              <TertiaryButton asChild size="sm">
                <RouterLink to={routePaths.settings.path}>
                  {t('page:procurementPlanList.createDialog.openSettings')}
                </RouterLink>
              </TertiaryButton>
            </AlertActions>
          </Alert>
        )}

        {/* 承認フロー非適用の注記 (AC-007, #6526)。本ダイアログ経由の作成は
            常に「承認なし」固定で承認フロー設定 UI を持たないことの説明であり
            注意喚起ではないため status="info"。表示位置・
            目立ち方は spec 上デザイナー裁量 (`plan-common.md:610`) — 先頭配置は
            spec の指定ではなく本設計の Confirmed Decision (design 未反映、
            designer 確認事項)。 */}
        <Alert
          description={t(
            'page:procurementPlanList.createDialog.approvalNoticeDescription',
          )}
          status="info"
          title={t('page:procurementPlanList.createDialog.approvalNoticeTitle')}
        />

        {/* 断面 (procurement-create-dialog-section-target-date-approval 設計
            Decision #1/#2: FIT 断面はこのダイアログに構造的に存在せず、選択肢が
            「当日・翌日・翌々日」の 1 つに統合されたため非操作の固定ラベル表示に変更
            (旧 Select は撤去、AC-PLC-039。#7149 で「当日」を含めるよう拡張)。 */}
        <FieldRoot>
          <FieldLabel>
            {t('page:procurementPlanList.createDialog.section')}
          </FieldLabel>
          <div style={{ fontSize: fontSizes.md.value }}>
            {t('page:procurementPlanList.createDialog.sectionValue')}
          </div>
        </FieldRoot>

        {/* 対象日 (日付入力は共通 CompactCivilDateField、2026-08-12 デザイナー指摘:
            Date picker 未使用箇所の DS 化。popup は zIndex=10 の絶対配置で
            Modal 内クリップ無し、既存 OCCTO マスタ modal 群と同じ構成で確認済み)。
            開いた瞬間に initialTargetDate (一覧の対象日フィルタ現在値) を初期値と
            してコピーし (AC-PLC-040、issue #7265)、手動でクリアした場合の未入力・
            範囲外 (過去日、初期値コピーが過去日の場合を含む) いずれも「作成」で
            エラー表示する (未入力は AC-PLC-063。AC-PLC-046 は「キャンセル/×で閉じる」
            で別内容)。許容範囲は今日以降の任意の未来日
            (AC-PLC-039/040、issue #6816 Discussion 決定行 B 案採用、#7149)。 */}
        <FieldRoot invalid={targetDateFieldError !== undefined} required>
          <FieldLabel htmlFor="createPlanDialog-targetDate">
            {t('page:procurementPlanList.createDialog.targetDate')}
            <RequiredMark />
          </FieldLabel>
          <CompactCivilDateField
            hasError={targetDateFieldError !== undefined}
            id="createPlanDialog-targetDate"
            onChange={(d) => {
              setTargetDate(d !== undefined ? civilDateToDate(d) : undefined)
              setTargetDateError(undefined)
            }}
            value={
              targetDate !== undefined ? dateToCivilDate(targetDate) : undefined
            }
          />
          {targetDateFieldError !== undefined && (
            <FieldErrorText>{targetDateFieldError}</FieldErrorText>
          )}
        </FieldRoot>

        {/* エリア。 候補が 0 件の時の案内は本文上部の Alert か「対象日」 の欄のエラー (AC-PLC-061) */}
        <FieldRoot invalid={isAreaInvalid} required>
          <FieldLabel>
            {t('page:procurementPlanList.createDialog.area')}
            <RequiredMark />
          </FieldLabel>
          {/* size は DS の既定 (chakra-react-select の md = 40px) と同値だが明示する。
              8080 の `.ecl-select--sm` は **height 40px** で基底と同じで、class 名の
              `sm` は DS の size="sm" を指さない。 既定に頼ると DS 側が既定を変えた時に
              8080 と無言でズレるため、 40px を意図として固定する。 */}
          <Select
            aria-label={t('page:procurementPlanList.createDialog.area')}
            isMulti={false}
            onChange={(opt) => {
              if (opt === null) return
              setSelection({
                areaCode: Number(opt.value) as AreaCode,
                bgCode: '',
              })
              // BG は選び直しになるので、 前回の「作成」 のエラーは持ち越さない
              setShowSelectionError(false)
            }}
            options={areaOptions}
            placeholder={t(
              'page:procurementPlanList.createDialog.areaPlaceholder',
            )}
            selectedOptions={selectedAreaOption}
            size="md"
          />
          {isAreaInvalid && (
            <FieldErrorText>{selectionErrorText}</FieldErrorText>
          )}
        </FieldRoot>

        {/* BG 選択 */}
        <FieldRoot invalid={isBgInvalid} required>
          <FieldLabel>
            {t('page:procurementPlanList.createDialog.bg')}
            <RequiredMark />
          </FieldLabel>
          {/* size は DS の既定 (chakra-react-select の md = 40px) と同値だが明示する。
              8080 の `.ecl-select--sm` は **height 40px** で基底と同じで、class 名の
              `sm` は DS の size="sm" を指さない。 既定に頼ると DS 側が既定を変えた時に
              8080 と無言でズレるため、 40px を意図として固定する。 */}
          <Select
            aria-label={t('page:procurementPlanList.createDialog.bg')}
            isDisabled={areaCode === AreaCode.UNSPECIFIED}
            isMulti={false}
            onChange={(opt) => {
              setSelection({ areaCode, bgCode: opt?.value ?? '' })
            }}
            options={bgOptions}
            placeholder={t(
              'page:procurementPlanList.createDialog.bgPlaceholder',
            )}
            selectedOptions={selectedBgOption}
            size="md"
          />
          {isBgInvalid && <FieldErrorText>{selectionErrorText}</FieldErrorText>}
        </FieldRoot>
      </div>
    </Modal>
  )
}

/** 対象日 (未入力なら絞らない) に有効な需要調達計画マスタ BG (AC-PLC-041-1 / 047)。 */
function activeItemsOn(
  items: DemandProcurementBgItem[],
  periods: ReadonlyMap<string, BgValidityPeriod>,
  date: Date | undefined,
): DemandProcurementBgItem[] {
  if (date === undefined) return items
  const civilDate = dateToCivilDate(date)
  return items.filter((item) =>
    isDemandProcurementBgActiveOn(civilDate, item, periods),
  )
}

/**
 * 選択可能エリア = 業務エリア設定内 AND BG マスタ登録済み (AC-09)。
 * AREA_CODE_OPTIONS は北→南の canonical 順を保つ。
 */
function selectableAreas(
  activeItems: DemandProcurementBgItem[],
  businessAreaSet: ReadonlySet<AreaCode>,
): AreaCode[] {
  const registeredAreaDigits = new Set(
    activeItems.map((item) => item.bgCode.slice(-1)),
  )
  return AREA_CODE_OPTIONS.filter(
    (area) =>
      businessAreaSet.has(area) &&
      registeredAreaDigits.has(occtoAreaDigit(area) ?? ''),
  )
}
